/**
 * CVGeneratorInline - Inline CV generation for a pipeline job.
 * Checks daily quota, generates CV via cv.service, shows preview + export.
 */

import { useState, useCallback } from 'react';
import { Button, Alert, useToast } from '@/components/common';
import { CVPreview } from '@/components/cv';
import { cvService, type ExportFormat, type GeneratedCV } from '@/services/cv.service';
import { useDailyQuotaStore } from '@/store/daily-quota.store';
import { usePipelineStore } from '@/store/pipeline.store';
import type { CVTemplate } from '@/components/cv/TemplatePreview';

interface CVGeneratorInlineProps {
  jobId: string;
  vacancyId: string;
}

const TEMPLATES: { id: CVTemplate; label: string }[] = [
  { id: 'professional', label: 'Professional' },
  { id: 'modern', label: 'Modern' },
  { id: 'twoColumn', label: 'Two Column' },
  { id: 'harvard', label: 'Harvard' },
];

export function CVGeneratorInline({ jobId, vacancyId }: CVGeneratorInlineProps) {
  const toast = useToast();
  const attachCV = usePipelineStore((s) => s.attachCV);
  const canGenerate = useDailyQuotaStore((s) => s.canGenerate);
  const consumeGeneration = useDailyQuotaStore((s) => s.consumeGeneration);
  const getRemainingGenerations = useDailyQuotaStore((s) => s.getRemainingGenerations);

  const [selectedTemplate, setSelectedTemplate] = useState<CVTemplate>('professional');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCV, setGeneratedCV] = useState<GeneratedCV | null>(null);
  const [renderedHTML, setRenderedHTML] = useState('');
  const [atsScore, setAtsScore] = useState<number | undefined>();
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const quotaAvailable = canGenerate();
  const remaining = getRemainingGenerations();

  const handleGenerate = useCallback(async () => {
    if (!quotaAvailable) return;

    setIsGenerating(true);
    setError(null);

    try {
      const options = {
        templateId: selectedTemplate,
        includePhoto: false,
      };

      const result = await cvService.generateCVForVacancy(vacancyId, options);

      // Consume quota after success
      consumeGeneration();

      // Attach CV to pipeline job
      attachCV(jobId, result.cv.id);

      setGeneratedCV(result.cv);
      setRenderedHTML(result.rendered);
      setAtsScore(result.atsScore);
      toast.success('CV generated successfully!');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'CV generation failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  }, [vacancyId, selectedTemplate, quotaAvailable, consumeGeneration, attachCV, jobId, toast]);

  const handleExport = useCallback(
    async (format: ExportFormat, template?: CVTemplate) => {
      if (!generatedCV) return;

      setIsExporting(true);
      try {
        const result = await cvService.exportCV(generatedCV.id, format, template);
        cvService.downloadFile(result.buffer, result.filename);
        toast.success(`CV exported as ${format.toUpperCase()}`);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Export failed.';
        toast.error(message);
      } finally {
        setIsExporting(false);
      }
    },
    [generatedCV, toast]
  );

  const handleStartOver = useCallback(() => {
    setGeneratedCV(null);
    setRenderedHTML('');
    setAtsScore(undefined);
    setError(null);
  }, []);

  return (
    <div className="cv-generator-inline">
      <h3 className="cv-generator-inline__title">CV Generation</h3>
      <p className="cv-generator-inline__subtitle">
        Generate a CV using your profile data, tailored to match this job's requirements
      </p>

      {/* Quota indicator */}
      <div
        className={`cv-generator-inline__quota ${
          quotaAvailable
            ? 'cv-generator-inline__quota--available'
            : 'cv-generator-inline__quota--exhausted'
        }`}
      >
        {quotaAvailable ? (
          <>
            <span className="cv-generator-inline__quota-count">{remaining}</span>
            <span>generations remaining today</span>
          </>
        ) : (
          <span>Daily limit reached. Try again tomorrow.</span>
        )}
      </div>

      {/* Generation UI (pre-generation state) */}
      {!generatedCV && !isGenerating && (
        <>
          {/* Template selector */}
          <div className="cv-generator-inline__template-section">
            <p className="cv-generator-inline__template-label">Template</p>
            <div className="cv-generator-inline__template-grid">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  className={`cv-generator-inline__template-btn ${
                    selectedTemplate === t.id
                      ? 'cv-generator-inline__template-btn--selected'
                      : ''
                  }`}
                  onClick={() => setSelectedTemplate(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Generate button */}
          <div className="cv-generator-inline__actions">
            <Button
              onClick={handleGenerate}
              disabled={!quotaAvailable}
              fullWidth
            >
              Generate CV for this job
            </Button>
            {quotaAvailable && remaining > 0 && (
              <p className="text-xs text-text-secondary text-center mt-1">
                Uses 1 of {remaining} remaining generation{remaining === 1 ? '' : 's'} today
              </p>
            )}
          </div>

          {error && (
            <div className="cv-generator-inline__error">
              <Alert variant="error">{error}</Alert>
            </div>
          )}
        </>
      )}

      {/* Loading state */}
      {isGenerating && (
        <div className="cv-generator-inline__loading">
          <div className="cv-generator-inline__spinner" />
          <p className="cv-generator-inline__loading-text">Generating your CV...</p>
          <p className="cv-generator-inline__loading-subtext">
            AI is optimizing your CV for this specific job. This may take up to 2 minutes.
          </p>
        </div>
      )}

      {/* Post-generation result */}
      {generatedCV && !isGenerating && (
        <div className="cv-generator-inline__result">
          <div className="cv-generator-inline__result-header">
            <p className="cv-generator-inline__result-title">CV Generated Successfully</p>
            <Button variant="ghost" size="sm" onClick={handleStartOver}>
              Generate New
            </Button>
          </div>
          <CVPreview
            cvId={generatedCV.id}
            htmlContent={renderedHTML}
            atsScore={atsScore}
            onExport={handleExport}
            isExporting={isExporting}
          />
        </div>
      )}
    </div>
  );
}
